/**
 * DQRulesPanel — Edit the dq_rules section of a job.
 *
 * One row per DQ rule: rule_name, type select (not_null/unique/custom_sql),
 * column input (for not_null/unique), expression input (for custom_sql).
 * Add/remove controls. Changes call PATCH .../dq-rules.
 */

import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import type {
  PEJob,
  DQRule,
  DQRulesPayload,
} from "../../../features/pipeline-engineer/types";
import { patchDQRules } from "../../../features/pipeline-engineer/api/pe-client";

// ---------------------------------------------------------------------------
// Internal row type
// ---------------------------------------------------------------------------

interface RuleRow {
  id: string;
  ruleName: string;
  type: "not_null" | "unique" | "custom_sql";
  column: string;
  expression: string;
}

function ruleToRow(r: DQRule, idx: number): RuleRow {
  return {
    id: `dq-${idx}-${r.ruleName}`,
    ruleName: r.ruleName,
    type: r.type,
    column: r.column ?? "",
    expression: r.expression ?? "",
  };
}

function rowsToPayload(rows: RuleRow[]): DQRulesPayload {
  return {
    dq_rules: rows.map((r) => ({
      rule_name: r.ruleName,
      type: r.type,
      column: r.type !== "custom_sql" ? r.column || null : null,
      expression: r.type === "custom_sql" ? r.expression || null : null,
    })),
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

const DQ_TYPES: Array<{
  value: "not_null" | "unique" | "custom_sql";
  label: string;
}> = [
  { value: "not_null", label: "Not Null" },
  { value: "unique", label: "Unique" },
  { value: "custom_sql", label: "Custom SQL" },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DQRulesPanelProps {
  job: PEJob;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (dirty: boolean) => void;
  onConflict: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DQRulesPanel({
  job,
  onSaved,
  onDirtyChange,
  onConflict,
}: DQRulesPanelProps) {
  const [rows, setRows] = useState<RuleRow[]>(() => job.dqRules.map(ruleToRow));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Compare rows (sans local `id`) against ruleToRow output — both camelCase to
  // avoid permanent-dirty from camelCase vs snake_case JSON key mismatch.
  useEffect(() => {
    const pick = (r: RuleRow) => ({
      ruleName: r.ruleName,
      type: r.type,
      column: r.column || null,
      expression: r.expression || null,
    });
    const original = JSON.stringify(job.dqRules.map(ruleToRow).map(pick));
    const current = JSON.stringify(rows.map(pick));
    onDirtyChange(original !== current);
  }, [rows, job.dqRules, onDirtyChange]);

  useEffect(() => {
    setRows(job.dqRules.map(ruleToRow));
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = useCallback((id: string, patch: Partial<RuleRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      {
        id: `dq-new-${Date.now()}`,
        ruleName: "",
        type: "not_null",
        column: "",
        expression: "",
      },
    ]);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await patchDQRules(
        job.pipelineId,
        job.id,
        rowsToPayload(rows),
      );
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409") || msg.toLowerCase().includes("conflict")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [rows, job.pipelineId, job.id, onSaved, onConflict]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-white/40">
        DQ rules run on the target table after each job load. Failures are
        informational — the job still completes successfully.
      </p>

      {rows.length === 0 ? (
        <p className="text-xs text-white/30 py-4 text-center">
          No DQ rules defined.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div
              key={row.id}
              className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/40">Rule {idx + 1}</span>
                <button
                  type="button"
                  aria-label={`Remove DQ rule ${idx + 1}`}
                  onClick={() => removeRow(row.id)}
                  className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                >
                  <Trash2 size={13} aria-hidden="true" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor={`dq-name-${row.id}`} className={LABEL_CLASS}>
                    Rule Name
                  </label>
                  <input
                    id={`dq-name-${row.id}`}
                    aria-label={`Rule name for row ${idx + 1}`}
                    type="text"
                    className={INPUT_CLASS}
                    placeholder="e.g. pk_not_null"
                    value={row.ruleName}
                    onChange={(e) =>
                      updateRow(row.id, { ruleName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label htmlFor={`dq-type-${row.id}`} className={LABEL_CLASS}>
                    Type
                  </label>
                  <select
                    id={`dq-type-${row.id}`}
                    aria-label={`Rule type for row ${idx + 1}`}
                    className={INPUT_CLASS}
                    value={row.type}
                    onChange={(e) =>
                      updateRow(row.id, {
                        type: e.target.value as
                          | "not_null"
                          | "unique"
                          | "custom_sql",
                      })
                    }
                  >
                    {DQ_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Column — visible for not_null / unique */}
              {(row.type === "not_null" || row.type === "unique") && (
                <div>
                  <label htmlFor={`dq-col-${row.id}`} className={LABEL_CLASS}>
                    Column
                  </label>
                  <input
                    id={`dq-col-${row.id}`}
                    type="text"
                    className={INPUT_CLASS}
                    placeholder="e.g. customer_id"
                    value={row.column}
                    onChange={(e) =>
                      updateRow(row.id, { column: e.target.value })
                    }
                  />
                </div>
              )}

              {/* Expression — visible for custom_sql */}
              {row.type === "custom_sql" && (
                <div>
                  <label htmlFor={`dq-expr-${row.id}`} className={LABEL_CLASS}>
                    SQL Expression
                  </label>
                  <input
                    id={`dq-expr-${row.id}`}
                    aria-label={`Expression for row ${idx + 1}`}
                    type="text"
                    className={`${INPUT_CLASS} font-mono text-xs`}
                    placeholder="e.g. status IN ('A', 'I')"
                    value={row.expression}
                    onChange={(e) =>
                      updateRow(row.id, { expression: e.target.value })
                    }
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors w-full justify-center"
      >
        <Plus size={13} aria-hidden="true" />
        Add Rule
      </button>

      {saveError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving && (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          )}
          Save DQ Rules
        </button>
      </div>
    </div>
  );
}
